import { Component, EventEmitter, Input, Output, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subscription } from 'rxjs';
import { AppState } from '../../../../../state/app.state';
import { PopupService } from '../../../../../services/popup.service';
import { ReportService } from '../../../../../services/report.service';
import { updateContactInfo, updateContactInfoSuccess, updateContactInfoFailure, updateReport } from '../../../../../state/search/search.actions';
import { selectContactInfoUpdateLoading } from '../../../../../state/search/search.selectors';
import { selectReport } from '../../../../../state/search/search.selectors';
import { take } from 'rxjs/operators';

@Component({
  selector: 'app-edit-contact-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTooltipModule],
  templateUrl: './edit-contact-modal.component.html',
  styleUrls: ['./edit-contact-modal.component.scss']
})
export class EditContactModalComponent implements OnChanges, OnDestroy {
  @Input() isVisible: boolean = false;
  @Input() emails: string[] = [];
  @Input() phoneNumbers: string[] = [];
  @Input() searchId: number;
  @Input() contactType: string = 'person';
  @Output() modalClosed = new EventEmitter<void>();
  @Output() contactUpdated = new EventEmitter<{ emails: string[]; phoneNumbers: string[]; contactType: string }>();

  editEmails: string[] = [];
  editPhoneNumbers: string[] = [];
  newEmail: string = '';
  newPhone: string = '';
  emailError: string = '';
  phoneError: string = '';

  loading$: Observable<boolean>;

  private subscriptions = new Subscription();

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private popupService: PopupService,
    private reportService: ReportService
  ) {
    this.loading$ = this.store.select(selectContactInfoUpdateLoading);

    this.subscriptions.add(
      this.actions$.pipe(ofType(updateContactInfoSuccess)).subscribe((action) => {
        console.log('[EditContactModal] Update successful', action);

        this.store.select(selectReport).pipe(take(1)).subscribe(reportResponse => {
          if (reportResponse?.report) {
            const currentReport = reportResponse.report;
            let updatedReport: any;

            if (this.contactType === 'person') {
              updatedReport = {
                ...currentReport,
                email: null,
                phone_numbers: [...this.editPhoneNumbers],
                personal_emails: [...this.editEmails],
                search_person: {
                  ...currentReport.search_person,
                  enhanced_data: {
                    ...currentReport.search_person?.enhanced_data,
                    email: null,
                    sanitized_phone: null,
                  }
                }
              };
            } else {
              // Company: update veridion_data sources
              const veridionData = currentReport.veridion_data;
              const updatedVeridionData = veridionData ? {
                ...veridionData,
                result: veridionData.result?.length > 0 ? [{
                  ...veridionData.result[0],
                  phone_numbers: [...this.editPhoneNumbers],
                  primary_email: this.editEmails[0] || null,
                  emails: [...this.editEmails],
                  other_emails: [],
                }] : veridionData.result
              } : veridionData;

              updatedReport = {
                ...currentReport,
                veridion_data: updatedVeridionData,
              };
            }

            this.store.dispatch(updateReport({ report: updatedReport }));
            this.reportService.updateReport(updatedReport);
          }
        });

        // Emit so parent components can update their local references
        this.contactUpdated.emit({
          emails: [...this.editEmails],
          phoneNumbers: [...this.editPhoneNumbers],
          contactType: this.contactType
        });

        this.popupService.notify('Contact information updated successfully!', 'Update success', 'success');
        this.closeModal();
      })
    );

    this.subscriptions.add(
      this.actions$.pipe(ofType(updateContactInfoFailure)).subscribe((error) => {
        console.error('Contact info update failed:', error);
        this.popupService.notify('Failed to update contact information', 'Update failed', 'error');
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible'] && this.isVisible) {
      this.editEmails = [...this.emails];
      this.editPhoneNumbers = [...this.phoneNumbers];
      this.newEmail = '';
      this.newPhone = '';
      this.emailError = '';
      this.phoneError = '';
    }
  }

  addEmail(): boolean {
    const email = this.newEmail.trim();
    this.emailError = '';

    if (!email) return true;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      this.emailError = 'Please enter a valid email address';
      return false;
    }

    if (this.editEmails.includes(email)) {
      this.emailError = 'This email already exists';
      return false;
    }

    this.editEmails.push(email);
    this.newEmail = '';
    return true;
  }

  removeEmail(index: number): void {
    this.editEmails.splice(index, 1);
  }

  addPhone(): boolean {
    const phone = this.newPhone.trim();
    this.phoneError = '';

    if (!phone) return true;

    if (phone.length > 50) {
      this.phoneError = 'Phone number is too long (max 50 characters)';
      return false;
    }

    if (this.editPhoneNumbers.includes(phone)) {
      this.phoneError = 'This phone number already exists';
      return false;
    }

    this.editPhoneNumbers.push(phone);
    this.newPhone = '';
    return true;
  }

  removePhone(index: number): void {
    this.editPhoneNumbers.splice(index, 1);
  }

  save(): void {
    const emailOk = this.addEmail();
    const phoneOk = this.addPhone();
    if (!emailOk || !phoneOk) return;

    this.store.dispatch(updateContactInfo({
      searchId: this.searchId,
      contactType: this.contactType,
      emails: this.editEmails,
      phones: this.editPhoneNumbers
    }));
  }

  closeModal(): void {
    this.isVisible = false;
    this.modalClosed.emit();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
